import styles from './styles.module.scss';

import React, {useEffect, useState} from 'react';
import {Alert, Button} from 'reactstrap';
import classNames from 'classnames';
import {isEmpty, isEqual, get} from 'lodash';

import useQuery from 'hooks/useQuery';
import LocalStorage, {removeItem} from 'utils/localStorage';
import {SUBSCRIPTION_TYPE_LEVEL} from 'configs/membership';
import {
  ENABLE_TEASER,
  ENABLE_ANALYTICS,
  ENABLE_ISO_SURVEY,
} from 'configs/enable-features';

import {useMembership} from 'contexts/MembershipContext';
import {useAuth} from 'contexts/AuthContext';
import MuiIcon from 'components/Icons/MuiIcon';

import {buildUrlParams} from 'utils/string';

import {ACCOUNT_ROLES} from 'configs';
const MIenabled = ENABLE_ANALYTICS && ENABLE_ISO_SURVEY;

function bannerContentBuilder(props) {
  const {
    isSubscribedToMI,
    currentPlanType,
    isV1,
    isManualMethod,
    isAdminOrStaff,
  } = props;

  const BANNER_CONTENT = {
    paid: MIenabled
      ? {
          message: (
            <>
              <strong>Market Intelligence is here!</strong> Get daily market
              insights from IAF CertSearch Analytics and explore long-term ISO
              Historical Data year by year, all in one place.
            </>
          ),
          cta: 'Explore Now',
          redirect: '/analytics/certifications',
        }
      : {
          message: (
            <>
              <strong>Enhanced Search Is Here!</strong> Find companies and
              certificates faster with direct search, more suggested results,
              and enhanced filters.
            </>
          ),
          cta: 'Try Now',
          redirect: '/advanced-search',
        },
    free: MIenabled
      ? {
          message: (
            <>
              <strong>Market Intelligence is here!</strong> Get daily market
              insights from IAF CertSearch Analytics and explore long-term ISO
              Historical Data year by year, all in one place.
            </>
          ),
          cta:
            isV1 || isManualMethod ? 'Contact Support' : 'Get Instant Access',
          redirect:
            isV1 || isManualMethod
              ? '/contact'
              : buildUrlParams('/membership/plans/checkout', {
                  plan:
                    currentPlanType === 'free'
                      ? 'pay_as_you_go'
                      : currentPlanType,
                  market_intelligence: true, //temporary redirect to membership
                  redirect: '/analytics/certifications',
                  source: 'banner',
                }),
        }
      : {
          message: (
            <>
              <strong>Enhanced Search Is Here!</strong> Find companies and
              certificates faster with direct search, more suggested results,
              and enhanced filters.
            </>
          ),
          cta: 'Try Now',
          redirect: '/advanced-search',
        },
    staff_admin: {
      message: (
        <>
          <strong>Market Intelligence is here!</strong> Get daily market
          insights from IAF CertSearch Analytics and explore long-term ISO
          Historical Data year by year, all in one place.
        </>
      ),
    },
  };

  if (isAdminOrStaff) {
    return BANNER_CONTENT['staff_admin'];
  }

  return BANNER_CONTENT[isSubscribedToMI ? 'paid' : 'free'];
}

function PageAlerts(props) {
  const {headerHeight} = props;
  const {query} = useQuery();
  const {isAuth, token, account} = useAuth();
  const {
    subscriptionLevel,
    isLegacyUser,
    isSubscribedToMI,
    billingMethod,
    isSuspendedSubscription,
    isProcessingPayment,
  } = useMembership();

  const isManualMethod = billingMethod === 'manual';

  const isEnterprise = isEqual(
    subscriptionLevel,
    SUBSCRIPTION_TYPE_LEVEL.enterprise
  );
  const isFree = isEqual(subscriptionLevel, SUBSCRIPTION_TYPE_LEVEL.free);

  const isV1 = isLegacyUser && !isEnterprise && !isFree;

  const accountRole = get(account, 'account_role');
  const adminOrStaff = get(account, 'account_role');

  const isAdminOrStaff = accountRole === 'admin' || accountRole === 'staff';

  const {success} = query || {};

  const storedData = LocalStorage.showTeaser;
  const [shown, setIsShown] = useState(storedData.show);

  const currentPlanType = get(
    account,
    'company.subscription.subscription_plans_type'
  );

  function handleClose() {
    setIsShown(false);
    LocalStorage.showTeaser = {...storedData, show: false};
  }

  useEffect(() => {
    setIsShown(storedData.show);
  }, [storedData]);

  useEffect(() => {
    if (isAuth) {
      const storedData = LocalStorage.showTeaser;
      const {token: storedToken} = storedData || {};

      // uses the token for alert visibility during session
      if (!isEqual(token, storedToken) && ENABLE_TEASER) {
        LocalStorage.showTeaser = {
          token,
          show: true,
        };
      }

      if (!isEmpty(LocalStorage.showTeaser) && !ENABLE_TEASER) {
        removeItem('showTeaser');
      }
    } else {
      // removes upon logout
      removeItem('showTeaser');
    }
  }, [isAuth]);

  if (success || isProcessingPayment) return null;

  const bannerContent = bannerContentBuilder({
    isSubscribedToMI,
    currentPlanType,
    isV1,
    isManualMethod,
    isAdminOrStaff,
  });

  return (
    <>
      {shown && !isSuspendedSubscription && (
        <Alert
          className={classNames(styles['page-alerts'])}
          style={{
            top: headerHeight,
          }}
        >
          <div className={classNames(styles['page-alerts--content'])}>
            <div className={styles['page-alerts--content--text']}>
              <MuiIcon
                name="campaign"
                size="lg"
                style={{verticalAlign: 'middle'}}
                className={styles['page-alerts--icon']}
              />

              <span className="flex-grow-1">{bannerContent.message}</span>
            </div>
            <div>
              <a
                href={bannerContent.redirect}
                target="_self"
                rel="noopener noreferrer"
              >
                {MIenabled ? (
                  adminOrStaff === ACCOUNT_ROLES.super_admin ||
                  (adminOrStaff !== ACCOUNT_ROLES.super_admin &&
                    isSubscribedToMI &&
                    bannerContent.cta) ? (
                    <Button
                      className={styles['page-alerts--content--cta']}
                      color="white"
                    >
                      <span>{bannerContent.cta}</span>
                      {/* <MuiIcon name="arrow_forward" /> */}
                    </Button>
                  ) : null
                ) : (
                  <Button
                    className={styles['page-alerts--content--cta']}
                    color="white"
                  >
                    <span className="mr-2">{bannerContent.cta}</span>
                    <MuiIcon name="arrow_forward" />
                  </Button>
                )}
              </a>
            </div>
          </div>

          <div>
            <MuiIcon
              name="clear"
              onClick={() => handleClose()}
              className={styles['page-alerts--icon-close']}
            />
          </div>
        </Alert>
      )}
    </>
  );
}

export default PageAlerts;
